import { Group, Label } from '@tlx/atlas';
import { useState } from 'react';
import { SalaryOnboardingTips } from '@Page/Onboarding/SalaryOnboardingTips';
import { SalaryOnboardingStageForm } from '@Page/Onboarding/SalaryOnboardingStage';
import { APIError, ValidationMessage } from '@tlx/astro-shared';
import { ValidatedFormInput } from '@Page/Onboarding/components/ValidatedForm/ValidatedInput';
import { rangeConstraintValidator } from '@Page/Onboarding/validators/rangeConstraintValidator';
import { NormalTimeDTO, updateNormalTime, useLatestNormalTime } from '@Page/Onboarding/hooks/useLatestNormalTime';
import { OnboardingStageSkeleton } from '@Page/Onboarding/components/OnboardingStageSkeleton';
import { setCustomValidityOnField } from '@Page/Onboarding/components/ValidatedForm/setCustomValidityOnField';
import { SalaryOnboardingTipsContainer } from '@Page/Onboarding/SalaryOnboardingWithTips';
import { FormattedMessage, useIntl } from 'react-intl';
import { JumboRadio, useSharedTracking } from '@tlx/beehive-shared';
import { JumboRadioGroup } from '@Page/Onboarding/JumboRadioGroup';
import { createOrdinalFromArray } from '@Util/misc.ts';

function mapToNormalTimeValues(dto: NormalTimeDTO) {
    return {
        normalTime: String(dto.hoursPerDay),
    };
}

function mapToNormalTimeDTO(formData: Partial<NormalTimeStageFormValues>) {
    return {
        hoursPerDay: formData.normalTime ? Number(formData.normalTime.replace(',', '.')) : undefined,
    };
}

export function ConnectedNormalTimeStage() {
    const { data, isLoading } = useLatestNormalTime();
    if (!data || isLoading) {
        return <OnboardingStageSkeleton />;
    }

    const id = data.id;
    const defaultValue = mapToNormalTimeValues(data);

    return (
        <NormalTimeStage
            defaultValue={defaultValue}
            handleSubmit={async (formData) => {
                const dto = mapToNormalTimeDTO(formData);
                await updateNormalTime(id, dto);
            }}
            handleError={(form, error) => {
                const unhandled: ValidationMessage[] = [];

                //TODO: need to map message.field to formfield name.
                error.details.validationMessages?.forEach((message) => {
                    if (message.field) {
                        const handled = setCustomValidityOnField(
                            form,
                            message.field === 'hoursPerDay' ? 'normalTime' : '',
                            message.message,
                        );
                        if (!handled) {
                            unhandled.push(message);
                        }
                    }
                });
                return unhandled;
            }}
        />
    );
}

type NormalTimeStageFormValues = {
    normalTime: string;
};

type NormalTimeStageProps = {
    defaultValue: NormalTimeStageFormValues;
    handleSubmit?: (formData: Partial<NormalTimeStageFormValues>) => Promise<void>;
    handleError?: (form: HTMLFormElement, error: APIError) => ValidationMessage[] | void;
};

export function NormalTimeStage({ defaultValue, handleError, handleSubmit }: NormalTimeStageProps) {
    const intl = useIntl();
    const sharedTrackingNormalTimeCustom = useSharedTracking('normal-time-custom');

    const isDefaultCustom = defaultValue.normalTime !== '8' && defaultValue.normalTime !== '7.5';
    const [normalTime, setNormalTime] = useState(isDefaultCustom ? 'custom' : defaultValue.normalTime);

    return (
        <>
            <SalaryOnboardingStageForm
                title={intl.formatMessage({
                    id: 'tsk_onboarding_standard_time_title',
                })}
                description={intl.formatMessage({
                    id: 'tsk_onboarding_standard_time_description',
                })}
                handleSubmit={handleSubmit}
                handleError={handleError}
            >
                <JumboRadioGroup name="normalTime">
                    <JumboRadio
                        value="8"
                        data-testid="normalTime-8-radio"
                        checked={normalTime === '8'}
                        onClick={() => setNormalTime('8')}
                    >
                        <div>
                            <div style={{ marginBlock: 10 }} className="atl-text-xl atl-font-medium">
                                <FormattedMessage
                                    id={'tsk_onboarding_standard_time_x_hours'}
                                    values={createOrdinalFromArray(['8'])}
                                />
                            </div>
                        </div>
                    </JumboRadio>
                    <JumboRadio
                        value="7.5"
                        data-testid="normalTime-7.5-radio"
                        checked={normalTime === '7.5'}
                        onClick={() => setNormalTime('7.5')}
                    >
                        <div>
                            <div style={{ marginBlock: 10 }} className="atl-text-xl atl-font-medium">
                                <FormattedMessage
                                    id={'tsk_onboarding_standard_time_x_hours'}
                                    values={createOrdinalFromArray(['7,5'])}
                                />
                            </div>
                        </div>
                    </JumboRadio>
                    <JumboRadio
                        value="custom"
                        data-testid="normalTime-custom-radio"
                        checked={normalTime === 'custom'}
                        onClick={() => setNormalTime('custom')}
                    >
                        <div>
                            <div style={{ marginBlock: 10 }} className="atl-text-xl atl-font-medium">
                                {intl.formatMessage({
                                    id: 'text_custom',
                                })}
                            </div>
                        </div>
                    </JumboRadio>
                </JumboRadioGroup>

                {normalTime === 'custom' && (
                    <Group className="atl-w-full">
                        <Label htmlFor="normalTimeCustom">
                            {intl.formatMessage({
                                id: 'text_employee_hours',
                            })}
                        </Label>
                        <ValidatedFormInput
                            className="atl-w-full"
                            id="normalTimeCustom"
                            type="text"
                            inputMode="decimal"
                            name="normalTime"
                            data-testid={sharedTrackingNormalTimeCustom}
                            defaultValue={defaultValue.normalTime}
                            disabled={normalTime !== 'custom'}
                            validator={(target) =>
                                rangeConstraintValidator(target.value.replace(',', '.'), {
                                    message: intl.formatMessage(
                                        {
                                            id: 'tsk_onboarding_validation_range_x_y',
                                        },
                                        createOrdinalFromArray([0, 24]),
                                    ),
                                    min: 0,
                                    max: 24,
                                })
                            }
                        />
                    </Group>
                )}
            </SalaryOnboardingStageForm>
            <SalaryOnboardingTipsContainer stageId="normal-time">
                <SalaryOnboardingTips
                    title={intl.formatMessage({
                        id: 'tsk_onboarding_standard_time_title',
                    })}
                    tag="Informasjon"
                >
                    {intl.formatMessage({
                        id: 'tsk_onboarding_standard_time_tip_description',
                    })}
                </SalaryOnboardingTips>
            </SalaryOnboardingTipsContainer>
        </>
    );
}
